<template>
    <div class="notification-panel" v-show="visible">
        <div class="notification-header">
            <div class="notification-tabs" role="tablist" aria-label="通知类型">
                <button
                    type="button"
                    role="tab"
                    class="tab-button"
                    :class="{ 'is-active': activeTab === categories.ALARM }"
                    :aria-selected="activeTab === categories.ALARM"
                    @click="activeTab = categories.ALARM"
                >
                    报警通知
                    <span class="tab-count" v-if="alarmCount > 0">{{ displayCount(alarmCount) }}</span>
                </button>
                <button
                    type="button"
                    role="tab"
                    class="tab-button"
                    :class="{ 'is-active': activeTab === categories.DEVICE_STATUS }"
                    :aria-selected="activeTab === categories.DEVICE_STATUS"
                    @click="activeTab = categories.DEVICE_STATUS"
                >
                    上下线
                    <span class="tab-count" v-if="deviceStatusCount > 0">
                        {{ displayCount(deviceStatusCount) }}
                    </span>
                </button>
            </div>
            <div class="header-actions">
                <el-button
                    v-if="activeCount > 0"
                    type="text"
                    size="mini"
                    @click="handleMarkAllRead"
                >
                    全部已读
                </el-button>
                <el-button type="text" size="mini" @click="handleViewAll">查看全部</el-button>
            </div>
        </div>

        <div class="notification-content" v-if="activeList.length > 0">
            <div
                v-for="(item, index) in activeList"
                :key="`${activeTab}-${item.notification_id || item.id || index}`"
                class="notification-item"
                :class="[getNotificationClass(item), { 'is-new': item.isNew }]"
                @click="handleItemClick(item)"
            >
                <div class="item-row item-row--head">
                    <span class="device-name" :title="item.device_name">{{ item.device_name }}</span>
                    <span class="time">{{ formatTime(item.event_time) }}</span>
                </div>

                <div v-if="isAlarmTab" class="item-row item-row--body">
                    <span class="alarm-meta">
                        <span class="value-label">{{ isRecovery(item) ? '恢复值' : '报警值' }}</span>
                        <span class="meta-value">{{ formatValue(item.event_value) }}</span>
                        <span class="alarm-description" :title="item.event_description || ''">
                            <span class="description-label">
                                {{ isRecovery(item) ? '恢复描述' : '报警描述' }}
                            </span>
                            <span class="description-value">{{ item.event_description || '-' }}</span>
                        </span>
                    </span>
                    <el-button
                        v-if="!isRecovery(item)"
                        type="text"
                        size="mini"
                        class="process-btn"
                        @click.stop="handleProcess(item)"
                    >
                        处理
                    </el-button>
                </div>

                <div v-else class="item-row item-row--status">
                    <span
                        class="event-status"
                        :class="isDeviceOnline(item) ? 'is-online' : 'is-offline'"
                    >
                        <i :class="isDeviceOnline(item) ? 'el-icon-circle-check' : 'el-icon-remove-outline'"></i>
                        {{ isDeviceOnline(item) ? '设备上线' : '设备离线' }}
                    </span>
                    <span class="device-imei" :title="item.device_imei">
                        IMEI {{ item.device_imei || '-' }}
                    </span>
                </div>
            </div>
        </div>

        <div class="notification-empty" v-else>
            <i :class="isAlarmTab ? 'el-icon-bell' : 'el-icon-connection'"></i>
            <p>{{ isAlarmTab ? '暂无报警通知' : '暂无设备上下线通知' }}</p>
        </div>
    </div>
</template>

<script>
import { formatDistanceToNow } from 'date-fns'
import { zhCN } from 'date-fns/locale'

import {
    ALARM_EVENT_TYPE,
    DEVICE_STATUS_EVENT_TYPE,
    NOTIFICATION_CATEGORY,
} from '@/config/enums.js'

export default {
    name: 'NotificationPanel',
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        alarmList: {
            type: Array,
            default: () => [],
        },
        alarmCount: {
            type: Number,
            default: 0,
        },
        deviceStatusList: {
            type: Array,
            default: () => [],
        },
        deviceStatusCount: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            activeTab: NOTIFICATION_CATEGORY.ALARM,
            categories: NOTIFICATION_CATEGORY,
        }
    },
    computed: {
        isAlarmTab() {
            return this.activeTab === NOTIFICATION_CATEGORY.ALARM
        },
        activeList() {
            return this.isAlarmTab ? this.alarmList : this.deviceStatusList
        },
        activeCount() {
            return this.isAlarmTab ? this.alarmCount : this.deviceStatusCount
        },
    },
    methods: {
        isRecovery(item) {
            return item.event_type === ALARM_EVENT_TYPE.RECOVER
        },
        isDeviceOnline(item) {
            return item.event_type === DEVICE_STATUS_EVENT_TYPE.ONLINE
        },
        getNotificationClass(item) {
            if (!this.isAlarmTab) {
                return this.isDeviceOnline(item) ? 'device-online' : 'device-offline'
            }
            if (this.isRecovery(item)) return 'alarm-recovered'
            const classMap = {
                1: 'alarm-normal',
                2: 'alarm-important',
                3: 'alarm-urgent',
            }
            return classMap[item.alarm_type] || ''
        },
        displayCount(count) {
            return count > 99 ? '99+' : count
        },
        formatValue(value) {
            if (value === null || value === undefined || value === '') return '-'
            const number = Number(value)
            return Number.isFinite(number) ? number.toFixed(2) : value
        },
        formatTime(time) {
            if (!time) return '-'
            try {
                const parsed = new Date(String(time).replace(/-/g, '/'))
                if (Number.isNaN(parsed.getTime())) return time
                return formatDistanceToNow(parsed, {
                    addSuffix: true,
                    locale: zhCN,
                })
            } catch (error) {
                return time
            }
        },
        handleViewAll() {
            this.$router.push(
                this.isAlarmTab
                    ? '/alarmCenter/alarmRecord'
                    : '/alarmCenter/deviceOnlineRecord',
            )
            this.$emit('close')
        },
        handleItemClick(item) {
            this.$emit(this.isAlarmTab ? 'item-click' : 'device-status-click', item)
        },
        handleProcess(item) {
            this.$emit('process', item)
        },
        handleMarkAllRead() {
            this.$emit('mark-all-read', this.activeTab)
        },
    },
}
</script>

<style scoped lang="less" src="./NotificationPanel.less"></style>
